import { useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, X } from 'lucide-react';
import { adsApi } from '@/api/client';
import {
  formatEnteredProductLookupMessage,
  parseAsinList,
  parseProductMetadataPayload,
  resolveEnteredAdvertisedProducts,
  splitEnteredProductIds,
} from '@/utils/spCampaignCreateDraft';
import { formatCurrency } from '@/utils/formatCurrency';
import {
  appendCollectionProducts,
  collectionProductKey,
  collectionProductMessage,
  moveCollectionProduct,
  SB_COLLECTION_MAX_PRODUCTS,
  SB_COLLECTION_MIN_PRODUCTS,
} from '@/utils/sbAdGroupCreate';
import type { SpAdvertisedProduct } from '@/types/spCampaignCreate';
import { Spinner } from '@/components/ui/Spinner';

function availabilityLabel(value: string): string {
  switch (value.toUpperCase()) {
    case 'IN_STOCK':
      return 'In stock';
    case 'OUT_OF_STOCK':
      return 'Out of stock';
    case 'IN_STOCK_SCARCE':
      return 'Low stock';
    default:
      return value.replace(/_/g, ' ');
  }
}

function productDetails(product: SpAdvertisedProduct): string | null {
  const parts: string[] = [];
  if (product.price != null) parts.push(formatCurrency(product.price));
  if (product.availability) parts.push(availabilityLabel(product.availability));
  if (product.rating != null) parts.push(`${product.rating} stars`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

async function lookupProductsByIds(
  profileId: string,
  asins: string[],
  skus: string[]
): Promise<SpAdvertisedProduct[]> {
  const requests: Promise<SpAdvertisedProduct[]>[] = [];
  const load = async (body: Record<string, unknown>) => {
    const res = await adsApi.getProductMetadata(profileId, {
      adType: 'SB',
      checkItemDetails: true,
      checkEligibility: true,
      pageIndex: 0,
      ...body,
    });
    if (!res.success) throw new Error(res.message || 'Could not load products.');
    return parseProductMetadataPayload(res.data);
  };
  if (asins.length) {
    requests.push(load({ asins, pageSize: Math.min(100, Math.max(25, asins.length)) }));
  }
  if (skus.length) {
    requests.push(load({ skus, pageSize: Math.min(100, Math.max(25, skus.length)) }));
  }
  const pages = await Promise.allSettled(requests);
  return pages.flatMap((page) => (page.status === 'fulfilled' ? page.value : []));
}

export function SbCollectionProducts({
  profileId,
  products,
  onChange,
  disabled,
}: {
  profileId: string;
  products: SpAdvertisedProduct[];
  onChange: (products: SpAdvertisedProduct[]) => void;
  disabled?: boolean;
}) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const addTyped = async () => {
    const raw = text.trim();
    if (!raw || disabled) return;
    const ids = parseAsinList(raw);
    if (ids.length === 0) {
      setMessage('Enter at least one ASIN or SKU.');
      return;
    }
    setLoading(true);
    setMessage(null);
    setNotice(null);
    try {
      const { asins, skus } = splitEnteredProductIds(ids);
      let metadata: SpAdvertisedProduct[] = [];
      try {
        metadata = await lookupProductsByIds(profileId, asins, skus);
      } catch {
        // Typed IDs still submit if lookup fails.
      }
      const resolved = resolveEnteredAdvertisedProducts({
        ids,
        metadata,
        catalog: [],
        existing: products,
      });
      const withAsin = resolved.toAdd.filter((product) => product.asin.trim());
      const result = appendCollectionProducts(products, withAsin);
      onChange(result.products);
      const lookupMessage = formatEnteredProductLookupMessage(resolved);
      const capMessage =
        result.trimmed > 0
          ? `A collection can feature at most ${SB_COLLECTION_MAX_PRODUCTS} products.`
          : null;
      setNotice(capMessage);
      setMessage(lookupMessage);
      if (ids.length > 0) setText('');
    } finally {
      setLoading(false);
    }
  };

  const countLabel = useMemo(
    () => `${products.length} product${products.length === 1 ? '' : 's'}`,
    [products.length]
  );
  const productWarning = collectionProductMessage(products);

  return (
    <div className="space-y-3">
      <p className="text-xs text-foreground-muted">
        Choose {SB_COLLECTION_MIN_PRODUCTS} to {SB_COLLECTION_MAX_PRODUCTS} products to feature in
        this ad. The first three appear on the ad. The full list is the landing page.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-border rounded-xl overflow-hidden">
        <div className="p-4 space-y-3 border-b lg:border-b-0 lg:border-r border-border">
          <p className="text-xs text-foreground-muted">
            Type ASINs or SKUs (one per line) and add them. Search is not required.
          </p>
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void addTyped();
              }
            }}
            placeholder="Enter ASINs or SKUs, one per line"
            className="w-full min-h-[88px] border border-border rounded-lg px-3 py-2 text-sm"
            disabled={disabled || loading}
            aria-label="ASINs or SKUs to feature"
          />
          <button
            type="button"
            className="text-sm text-brand disabled:opacity-40"
            disabled={disabled || loading || !text.trim()}
            onClick={() => void addTyped()}
          >
            {loading ? 'Adding…' : 'Add products'}
          </button>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-foreground-muted">
              <Spinner className="w-4 h-4" />
              Looking up products…
            </div>
          ) : null}
          {message ? <p className="text-xs text-foreground-muted">{message}</p> : null}
          {notice ? <p className="text-xs text-foreground-muted">{notice}</p> : null}
        </div>
        <div className="p-4 bg-page/60">
          <p className="text-sm font-medium mb-1">{countLabel}</p>
          <p className="text-xs text-foreground-muted mb-3">
            Add three to ten products to feature in your ad. Use the arrows to change the display
            order.
          </p>
          {productWarning ? (
            <div className="mb-3 flex items-start gap-2 text-sm text-error bg-error-subtle border border-error/20 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
              <span>{productWarning}</span>
            </div>
          ) : null}
          {products.length === 0 ? (
            <p className="text-sm text-foreground-subtle border border-dashed border-border rounded-lg p-4">
              No products selected.
            </p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg bg-surface max-h-80 overflow-auto">
              {products.map((product, index) => {
                const details = productDetails(product);
                return (
                  <li key={collectionProductKey(product)} className="flex items-center gap-2 p-2">
                    <div className="flex flex-col">
                      <button
                        type="button"
                        aria-label="Move product up"
                        disabled={disabled || index === 0}
                        className="text-foreground-muted disabled:opacity-30"
                        onClick={() => onChange(moveCollectionProduct(products, index, -1))}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Move product down"
                        disabled={disabled || index === products.length - 1}
                        className="text-foreground-muted disabled:opacity-30"
                        onClick={() => onChange(moveCollectionProduct(products, index, 1))}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt="" className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-surface-muted" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{product.title || product.asin || product.sku}</p>
                      <p className="text-xs text-foreground-muted">{product.asin || product.sku}</p>
                      {details ? <p className="text-xs text-foreground-muted">{details}</p> : null}
                    </div>
                    <button
                      type="button"
                      disabled={disabled}
                      aria-label="Remove product"
                      onClick={() =>
                        onChange(
                          products.filter(
                            (item) => collectionProductKey(item) !== collectionProductKey(product)
                          )
                        )
                      }
                    >
                      <X className="w-4 h-4 text-foreground-muted" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
